import { useEffect, useRef } from 'react';
import { Link, useRouteContext } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import {
	motion,
	animate,
	useMotionValue,
	useTransform,
	useAnimationFrame,
	useReducedMotion,
	type MotionValue,
	type PanInfo,
} from 'motion/react';
import { Image } from '@/routes/-components/image';
import { diamondGlows, DiamondGrid } from './primitives';

/**
 * "Empresas que ya generan impacto con Worldcoo" — the partner-merchant logo wheel.
 * Logos are REAL merchants from `getWorldcooGlobalStats` → `partnerMerchants` (`{ id, name,
 * portalId, logo }`), already filtered upstream to merchants running a campaign *right now* and
 * ordered by amount raised (biggest first). Only merchants with a logo are rendered; those with a
 * `portalId` link to their public donor portal (`/{portalId}`), the rest are shown but not clickable.
 *
 * Desktop is a single draggable "ferris wheel": one shared belt scrolls up the right side and
 * down the left (a single loop, not two independent columns), auto-advancing slowly and
 * grab-to-spin with inertia. Mobile keeps two horizontal marquee rows.
 */
export type PartnerMerchant = { id: string; name?: string; portalId?: string; logo?: { location: string } };

function LogoCard({ merchant }: { merchant: PartnerMerchant }) {
	// Uniform card; the belt controls colour/scale one level up via `filter`/`scale`.
	// `pointer-events-none` so a press on the logo (image) doesn't start a native image-drag that
	// would swallow the wheel gesture — the press bubbles to the wheel (spins) while the click
	// still lands on the wrapping <a> (navigates).
	//
	// The logo box is generous on BOTH axes because `object-contain` fits to whichever runs out first:
	// widening alone leaves square marks small, heightening alone leaves wide lockups small. Keep the
	// card size in sync with CARD_W/CARD_H.
	return (
		<div className="pointer-events-none flex h-24 w-56 items-center justify-center rounded-2xl bg-white px-5 shadow-[0_8px_24px_0_rgba(77,33,0,0.06)] select-none">
			{merchant.logo ? (
				<Image
					src={merchant.logo.location}
					alt={merchant.name ?? merchant.id}
					height={64}
					aspectRatio={184 / 64}
					objectFit="contain"
					className="max-h-16 w-full object-contain!"
				/>
			) : (
				<span className="text-ink truncate text-sm font-semibold">{merchant.name}</span>
			)}
		</div>
	);
}

/**
 * Absolute URL of a merchant's public donor portal. Portals live on a subdomain
 * (`correos.worldcoo.com`), NOT a path on the current host — so we take the current origin and
 * swap its first host label for the portalId, preserving domain, port and scheme across
 * environments (`new.localhost:3001` → `correos.localhost:3001`, `new.worldcoo.com` →
 * `correos.worldcoo.com`).
 */
function buildPortalHref(baseUrl: string, portalId: string): string {
	const sep = baseUrl.indexOf('://');
	if (sep === -1) return `/${portalId}`;
	const scheme = baseUrl.slice(0, sep + 3);
	const rest = baseUrl
		.slice(sep + 3)
		.split('.')
		.slice(1)
		.join('.');
	return rest ? `${scheme}${portalId}.${rest}` : `/${portalId}`;
}

/**
 * Wraps a logo in a link to the merchant's public portal when it has one; otherwise a plain,
 * non-interactive box. `guardDrag` cancels the click that a pointer-up fires at the end of a drag.
 */
function LogoLink({
	merchant,
	baseUrl,
	guardDrag,
	className,
}: {
	merchant: PartnerMerchant;
	baseUrl: string;
	guardDrag?: () => boolean;
	className?: string;
}) {
	if (!merchant.portalId) {
		return (
			<div className={className}>
				<LogoCard merchant={merchant} />
			</div>
		);
	}
	return (
		<a
			href={buildPortalHref(baseUrl, merchant.portalId)}
			aria-label={merchant.name ?? merchant.id}
			title={merchant.name}
			className={cn('block', className)}
			onClick={(e) => {
				if (guardDrag?.()) e.preventDefault();
			}}
			draggable={false}
		>
			<LogoCard merchant={merchant} />
		</a>
	);
}

// Belt geometry.
const COL_H = 576; // px — wheel viewport height (matches h-[576px]); ~5 cards visible per run
const CARD_W = 224; // px — card width (w-56, see LogoCard)
const CARD_H = 96; // px — card height (h-24, see LogoCard)
const SLOT_H = 112; // px — spacing between cards along the belt (COL_H / SLOT_H ≈ cards per run)
const ARC_AMPLITUDE = 96; // px the middle card bulges toward the center
const MIN_SCALE = 0.78; // scale of a card entering/leaving at top/bottom
const MAX_SCALE = 1.08; // scale of the card sitting at the arc's apex (center)
const AUTO_SPEED = -0.036; // px per ms of idle auto-advance (~36px/s)
const DRAG_THRESHOLD = 6; // px moved before a press counts as a drag (suppresses the click)
const RUN_INSET_RATIO = 0.11; // pull both runs this fraction of the width in from the edges (toward center)

type Geom = { x: number; y: number; scale: number; arc: number; on: boolean };

/**
 * Map a card's index + the shared belt offset `p` to screen coordinates. The belt is a closed
 * loop: the first half is the right run (travelling up), the second the left run (travelling
 * down). Cards hand off from one run to the other off-screen (top/bottom), so the switch of
 * horizontal side is never visible.
 */
function beltGeom(i: number, count: number, p: number, width: number): Geom {
	const L = count * SLOT_H;
	const runLen = L / 2;
	const bp = (((i * SLOT_H + p) % L) + L) % L;

	let side: 'right' | 'left';
	let yc: number; // card center, screen space (0 = top)
	if (bp < runLen) {
		side = 'right';
		yc = COL_H / 2 - bp + runLen / 2; // enters at the bottom, travels up
	} else {
		side = 'left';
		yc = COL_H / 2 - runLen / 2 + (bp - runLen); // enters at the top, travels down
	}

	const u = Math.min(1, Math.max(0, yc / COL_H));
	const arc = Math.sin(Math.PI * u); // 0 at edges → 1 at the vertical center
	// `<>` shape: the apex sits flush against the run's edge and the ends tuck toward the center.
	// Both runs are pulled in from the section edges toward the center by RUN_INSET_RATIO.
	const inset = width * RUN_INSET_RATIO;
	const tuck = (1 - arc) * ARC_AMPLITUDE;
	const baseX = side === 'right' ? width - CARD_W - inset : inset;
	const x = side === 'right' ? baseX - tuck : baseX + tuck;

	return {
		x,
		y: yc - CARD_H / 2,
		scale: MIN_SCALE + arc * (MAX_SCALE - MIN_SCALE),
		arc,
		on: yc > -CARD_H && yc < COL_H + CARD_H,
	};
}

function BeltCard({
	merchant,
	baseUrl,
	i,
	count,
	pos,
	widthRef,
	guardDrag,
}: {
	merchant: PartnerMerchant;
	baseUrl: string;
	i: number;
	count: number;
	pos: MotionValue<number>;
	widthRef: React.RefObject<number>;
	guardDrag: () => boolean;
}) {
	const geom = useTransform(pos, (p) => beltGeom(i, count, p, widthRef.current));
	const x = useTransform(geom, (g) => g.x);
	const y = useTransform(geom, (g) => g.y);
	const scale = useTransform(geom, (g) => g.scale);
	const opacity = useTransform(geom, (g) => (g.on ? 1 : 0));
	// Logos keep their own colours everywhere — Figma renders them monochrome in this wheel, but the
	// team wants partner brands in brand colour. Depth comes from opacity and scale alone; desaturating
	// as well is what made the outer logos look switched off rather than further away.
	const filter = useTransform(geom, (g) => `opacity(${0.55 + g.arc * 0.45})`);

	return (
		<motion.div className="absolute top-0 left-0" style={{ x, y, scale, opacity, filter }}>
			<LogoLink merchant={merchant} baseUrl={baseUrl} guardDrag={guardDrag} />
		</motion.div>
	);
}

/**
 * The desktop wheel: one shared belt offset drives every card. It auto-advances when idle and
 * can be grabbed and spun (with inertia). Reduced motion freezes the belt into a static snapshot.
 */
function LogoWheel({ merchants, baseUrl }: { merchants: PartnerMerchant[]; baseUrl: string }) {
	// Build the belt order first (pure) so the initial offset can center the top of the ranking.
	// Repeat the list when there are too few logos to overflow both runs (handoffs must stay
	// off-screen). Then INTERLEAVE: the belt splits into a right run and a left run ~half the
	// ranking apart, so feeding even ranks to the right run and odd ranks to the left makes BOTH
	// columns show the top raisers — otherwise one column always shows only the lower half.
	const MIN_ITEMS = 20;
	const filled =
		merchants.length > 0 && merchants.length < MIN_ITEMS
			? Array.from(
					{ length: merchants.length * Math.ceil(MIN_ITEMS / merchants.length) },
					(_, k) => merchants[k % merchants.length]!,
				)
			: merchants;
	const half = Math.ceil(filled.length / 2);
	const items = filled.map((_, i) => filled[i < half ? i * 2 : (i - half) * 2 + 1] ?? filled[i]!);
	const count = items.length;

	const reduce = useReducedMotion() ?? false;
	// Open the wheel on the biggest raiser: rank 0 sits at the right run's apex (largest, in colour)
	// and rank 1 at the left run's, so the top contributors are what you first see.
	const pos = useMotionValue((count * SLOT_H) / 4);
	const widthRef = useRef(1360);
	const trackRef = useRef<HTMLDivElement>(null);
	const interacting = useRef(false);
	const draggedRef = useRef(false);
	const inertia = useRef<{ stop: () => void } | null>(null);

	// Track the wheel width so cards can be placed against the right edge in px.
	useEffect(() => {
		const el = trackRef.current;
		if (!el) return;
		const update = () => {
			widthRef.current = el.clientWidth;
			pos.set(pos.get()); // nudge the transforms to recompute with the new width
		};
		update();
		const ro = new ResizeObserver(update);
		ro.observe(el);
		return () => ro.disconnect();
	}, [pos]);

	// Idle auto-advance (skipped while dragging / gliding, and under reduced motion).
	useAnimationFrame((_, delta) => {
		if (reduce || interacting.current) return;
		pos.set(pos.get() + delta * AUTO_SPEED);
	});

	const onPanStart = () => {
		interacting.current = true;
		draggedRef.current = false;
		inertia.current?.stop();
	};
	const onPan = (_: PointerEvent, info: PanInfo) => {
		if (Math.abs(info.offset.y) > DRAG_THRESHOLD) draggedRef.current = true;
		pos.set(pos.get() + info.delta.y);
	};
	const onPanEnd = (_: PointerEvent, info: PanInfo) => {
		inertia.current = animate(pos, pos.get(), {
			type: 'inertia',
			velocity: info.velocity.y,
			power: 0.7,
			timeConstant: 420,
			onComplete: () => {
				interacting.current = false;
			},
		});
	};

	if (count === 0) return <div className="hidden h-[576px] xl:block" />;

	const handlers = reduce ? {} : { onPanStart, onPan, onPanEnd };

	return (
		<motion.div
			ref={trackRef}
			className="relative hidden h-[576px] w-full touch-none overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_14%,black_86%,transparent)] select-none xl:block xl:cursor-grab xl:active:cursor-grabbing"
			onPointerDown={() => {
				draggedRef.current = false;
			}}
			{...handlers}
		>
			{items.map((merchant, i) => (
				<BeltCard
					// oxlint-disable-next-line react/no-array-index-key
					key={i}
					merchant={merchant}
					baseUrl={baseUrl}
					i={i}
					count={count}
					pos={pos}
					widthRef={widthRef}
					guardDrag={() => draggedRef.current}
				/>
			))}
		</motion.div>
	);
}

// One logo's footprint: the card (w-56) plus its right margin (mr-3).
const MOBILE_ITEM_W = 224 + 12;
// px per second. Matches the desktop wheel's idle speed so both read as the same motion.
const MOBILE_MARQUEE_SPEED = 32;

/**
 * Mobile: a horizontal row of logos scrolling forever. List duplicated + per-item right margin so
 * the `new-marquee` translateX(-50%) loops seamlessly. Logos with a portal link stay tappable.
 *
 * The duration is DERIVED from how many logos there are, not fixed. `translateX(-50%)` of a duplicated
 * list travels exactly one copy's width, so a constant duration means the speed scales with the number
 * of merchants — with a hardcoded 32s and the ~26 logos a row carries in production, the strip flew past
 * at ~190px/s, six times the desktop wheel. Tying duration to width pins the speed instead.
 *
 * The class keeps a duration so reduced-motion handling and a sane fallback stay in CSS; the inline
 * style overrides it, inline winning over the class.
 */
function LogoRowMobile({
	merchants,
	baseUrl,
	reverse,
}: {
	merchants: PartnerMerchant[];
	baseUrl: string;
	reverse?: boolean;
}) {
	if (merchants.length === 0) return null;
	const seconds = (merchants.length * MOBILE_ITEM_W) / MOBILE_MARQUEE_SPEED;
	return (
		<div className="overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
			<div
				style={{ animationDuration: `${Math.round(seconds)}s` }}
				className={cn(
					'flex w-max',
					reverse
						? 'motion-safe:animate-[new-marquee_32s_linear_infinite_reverse]'
						: 'motion-safe:animate-[new-marquee_32s_linear_infinite]',
				)}
			>
				{[...merchants, ...merchants].map((merchant, i) => (
					// oxlint-disable-next-line react/no-array-index-key
					<LogoLink key={i} merchant={merchant} baseUrl={baseUrl} className="mr-3 shrink-0" />
				))}
			</div>
		</div>
	);
}

/** Centered title + CTA, shared by the desktop overlay and the mobile layout. */
function Center() {
	const { t } = useLingui();
	return (
		<div className="flex flex-col items-center gap-6 text-center">
			<h2 className="max-w-md text-3xl font-medium tracking-tight md:text-4xl">
				{t`Companies already making an impact with Worldcoo`}
			</h2>
			<Link
				to="/new/contact"
				className="bg-brand hover:bg-brand-strong pointer-events-auto flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-5 font-medium text-black transition-colors"
			>
				{t`Contact us`}
				<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
					<ArrowUpRight className="size-4" />
				</span>
			</Link>
		</div>
	);
}

export function Partners({
	connector = true,
	...props
}: {
	merchants: PartnerMerchant[];
	/**
	 * The dangling line-and-dot below the section, which ties it to whatever comes next. It is part of
	 * the homepage composition, so pages that place this section elsewhere turn it off rather than
	 * leaving a thread pointing at a section it was never drawn for.
	 */
	connector?: boolean;
}) {
	const merchants = props.merchants;
	// Current origin (scheme + host), used to build each merchant's portal subdomain URL.
	const { baseUrl } = useRouteContext({ from: '__root__' });
	// Only merchants with a real logo make it into the wheel.
	const logos = merchants.filter((m) => m.logo);

	return (
		<section className="relative overflow-hidden bg-white">
			{/* Same glow shape as the hero, in the single position Figma gives this section: centred, 36px
			    above the middle. `opacity` 1 because the glow's own 0.4 alpha is the intensity. */}
			<DiamondGrid opacity={1} masks={[diamondGlows({ x: '50%', y: 'calc(50% - 36px)' })]} />

			<div className="relative mx-auto max-w-[1440px] px-6 py-20 md:py-28 xl:px-10">
				<div className="relative hidden xl:block">
					<LogoWheel merchants={logos} baseUrl={baseUrl} />
					<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
						<Center />
					</div>
				</div>

				<div className="xl:hidden">
					<Center />
					<div className="relative mt-8 flex flex-col gap-3">
						<LogoRowMobile merchants={logos.filter((_, i) => i % 2 === 0)} baseUrl={baseUrl} />
						<LogoRowMobile merchants={logos.filter((_, i) => i % 2 === 1)} baseUrl={baseUrl} reverse />
					</div>
				</div>
			</div>

			{connector && (
				<div className="relative mt-4 flex flex-col items-center xl:-mt-24" aria-hidden>
					<div className="from-brand/0 to-brand h-28 w-px bg-gradient-to-b md:h-36" />
					<div className="border-brand size-3 rounded-full border-2 bg-white" />
					<div className="bg-brand h-2.5 w-px" />
				</div>
			)}
		</section>
	);
}
